import React, { useEffect, useState } from 'react';

const PrintFinancialReport = () => {
    const [report, setReport] = useState(null);

    useEffect(() => {
        const storedReport = localStorage.getItem('print_financial_report');
        if (storedReport) {
            setReport(JSON.parse(storedReport));
        }
        
        const timer = setTimeout(() => {
            window.print();
        }, 800);

        return () => clearTimeout(timer);
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    if (!report) return <div className="text-center p-10">جاري تحميل البيانات...</div>;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const parts = dateString.split('T')[0].split('-');
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            return dateString;
        } catch (e) {
            return dateString;
        }
    };

    const { summary, expenses, revenues, academic_year, print_start_date, print_end_date } = report;
    
    const yearName = academic_year.name.split('-')[0] || academic_year.name;
    const startDate = print_start_date ? formatDate(print_start_date) : formatDate(academic_year.start_date);
    const endDate = print_end_date ? formatDate(print_end_date) : formatDate(academic_year.end_date);
    
    // Expenses
    const validExpenses = expenses.breakdown.filter(e => e.amount > 0);
    
    // Revenues - Remove "تغذية الخزينة"
    const treasuryFeedItem = revenues.breakdown.find(r => r.source === 'تغذية الخزينة');
    const treasuryFeedAmount = treasuryFeedItem ? parseFloat(treasuryFeedItem.amount) || 0 : 0;
    const validRevenues = revenues.breakdown.filter(r => r.amount > 0 && r.source !== 'تغذية الخزينة');
    const totalValidRevenuesAmount = validRevenues.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

    // Adjust totals
    const adjustedTotalRevenues = (parseFloat(summary.total_revenues_general) || 0) - treasuryFeedAmount;
    const adjustedNetResult = (parseFloat(summary.net_result) || 0) - treasuryFeedAmount;

    return (
        <div className="print-container">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img src="/logo.png" alt="Logo" style={{ width: '100px', height: '100px', objectFit: 'contain', display: 'block', margin: '0 auto 10px auto' }} />
            </div>

            <div className="header-text">
                <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px' }}>التقرير المالي لسنة {yearName}</div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>خلال الفترة الممتدة ما بين {startDate} إلى غاية {endDate}</div>
            </div>

            <table className="excel-table">
                <thead>
                    <tr>
                        <th style={{ width: '40%' }}>البيان</th>
                        <th style={{ width: '30%' }}>المبلغ</th>
                        <th style={{ width: '30%' }}>النسبة %</th>
                    </tr>
                </thead>
                <tbody>
                    {/* Expenses Section */}
                    <tr>
                        <td colSpan="3" className="text-center font-bold section-header">المصاريف</td>
                    </tr>
                    {validExpenses.map((expense, idx) => (
                        <tr key={`exp-${idx}`}>
                            <td className="text-center font-bold" style={{ textTransform: 'uppercase' }}>{expense.type_name}</td>
                            <td className="text-center font-bold">{formatCurrency(expense.amount)}</td>
                            <td className="text-center">{expense.percentage ? expense.percentage : ''}</td>
                        </tr>
                    ))}
                    {validExpenses.length === 0 && (
                        <tr>
                            <td colSpan="3" className="text-center" style={{ padding: '20px' }}>لا توجد مصاريف مسجلة</td>
                        </tr>
                    )}
                    <tr className="total-row">
                        <td className="text-center font-bold">مجموع المصاريف المسددة</td>
                        <td className="text-center font-bold">{formatCurrency(expenses.total)}</td>
                        <td className="text-center"></td>
                    </tr>

                    {/* Revenues Section */}
                    <tr>
                        <td colSpan="3" className="text-center font-bold section-header">المداخيل</td>
                    </tr>
                    <tr>
                        <td className="text-center font-bold">الرصيد السابق (مرحل)</td>
                        <td className="text-center font-bold">{formatCurrency(summary.previous_balance)}</td>
                        <td className="text-center"></td>
                    </tr>
                    {validRevenues.map((revenue, idx) => (
                        <tr key={`rev-${idx}`}>
                            <td className="text-center font-bold">{revenue.source}</td>
                            <td className="text-center font-bold">{formatCurrency(revenue.amount)}</td>
                            <td className="text-center">{totalValidRevenuesAmount > 0 ? (Math.round((parseFloat(revenue.amount) || 0) / totalValidRevenuesAmount * 10000) / 100) : ''}</td>
                        </tr>
                    ))}
                    {validRevenues.length === 0 && (
                        <tr>
                            <td colSpan="3" className="text-center" style={{ padding: '20px' }}>لا توجد مداخيل مسجلة</td>
                        </tr>
                    )}
                    <tr className="total-row">
                        <td className="text-center font-bold">مجموع المداخيل العامة</td>
                        <td className="text-center font-bold">{formatCurrency(adjustedTotalRevenues)}</td>
                        <td className="text-center"></td>
                    </tr>

                    {/* Final Result Box */}
                    <tr>
                        <td colSpan="3" style={{ height: '30px', border: 'none' }}></td>
                    </tr>
                    <tr className="final-result-row">
                        <td className="text-center font-bold">النتيجة الصافية (المداخيل - المصاريف)</td>
                        <td className="text-center font-bold" colSpan="2">{formatCurrency(adjustedNetResult)}</td>
                    </tr>
                </tbody>
            </table>

            <style dangerouslySetInnerHTML={{__html: `
                @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
                
                body {
                    margin: 0;
                    padding: 0;
                    background: white;
                }

                .print-container {
                    width: 80%;
                    min-height: 297mm;
                    padding: 20mm;
                    margin: 0 auto;
                    direction: rtl;
                    font-family: 'Cairo', Arial, sans-serif;
                    box-sizing: border-box;
                    color: #000;
                }

                .header-text {
                    text-align: center;
                    margin-bottom: 40px;
                }

                .excel-table {
                    width: 100%;
                    margin: 0 auto 10px auto;
                    border-collapse: collapse;
                    border: 1px solid #000;
                }

                .excel-table th, .excel-table td {
                    border: 1px solid #000;
                    padding: 8px 12px;
                    font-size: 16px;
                }

                .excel-table th {
                    text-align: center;
                    font-weight: bold;
                    background-color: #f0f0f0;
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }

                .section-header {
                    background-color: #e0e0e0;
                    font-size: 18px;
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }

                .total-row td {
                    background-color: #fff;
                    font-weight: bold;
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }

                .final-result-row td {
                    border: 3px solid #000 !important;
                    font-size: 18px;
                    padding: 12px;
                    background-color: #fff;
                    -webkit-print-color-adjust: exact;
                    color-adjust: exact;
                }

                .text-right { text-align: right !important; }
                .text-center { text-align: center !important; }
                .font-bold { font-weight: bold !important; }

                @media print {
                    @page { margin: 0; size: A4 portrait; }
                    body { background: none; }
                    .print-container { 
                        width: 100%;
                        height: 100%;
                        padding: 15mm; 
                        margin: 0;
                        box-shadow: none;
                    }
                }
            `}} />
        </div>
    );
};

export default PrintFinancialReport;
